import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarRange, MessageSquare, Plus, Send, Trash2, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

const STATUS_TONE = {
  pending: "var(--pc-score-medium)", needs_info: "var(--pc-score-medium)",
  approved: "var(--pc-score-good)", rejected: "var(--pc-score-poor)", cancelled: "var(--pc-muted)",
};
const currentYear = () => Number(new Intl.DateTimeFormat("en", {
  timeZone: "Europe/Berlin", year: "numeric",
}).format(new Date()));
const emptyPeriod = () => ({ start_date: "", end_date: "" });
const daysInPeriod = (period) => period.start_date && period.end_date
  ? Math.round((new Date(`${period.end_date}T12:00:00`) - new Date(`${period.start_date}T12:00:00`)) / 86400000) + 1
  : null;
const periodLabel = (period, lang) => {
  const locale = lang === "de" ? "de-DE" : "en-GB";
  const format = (value) => new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "2-digit", year: "numeric",
  }).format(new Date(`${value}T12:00:00`));
  return `${format(period.start_date)}–${format(period.end_date)}`;
};
const periodsOverlap = (left, right) => (
  left.start_date <= right.end_date && left.end_date >= right.start_date
);

export function analyzeLeavePeriods(periods, year, existingRows = [], lang = "de") {
  const accepted = [];
  const existing = existingRows.filter((row) => (
    ["pending", "needs_info", "approved"].includes(row.status)
    && row.start_date && row.end_date
  ));
  const analyses = periods.map((period) => {
    if (!period.start_date && !period.end_date) {
      return { complete: false, days: null, valid: false, message: "" };
    }
    if (!period.start_date || !period.end_date) {
      return {
        complete: false, days: null, valid: false,
        message: lang === "de" ? "Bitte Von und Bis vollständig angeben." : "Enter both From and To.",
      };
    }
    if (period.end_date < period.start_date) {
      return {
        complete: true, days: null, valid: false,
        message: lang === "de" ? "Das Bis-Datum darf nicht vor dem Von-Datum liegen." : "To must not be before From.",
      };
    }
    if (!period.start_date.startsWith(`${year}-`) || !period.end_date.startsWith(`${year}-`)) {
      return {
        complete: true, days: null, valid: false,
        message: lang === "de" ? `Der Zeitraum muss vollständig in ${year} liegen.` : `The period must be within ${year}.`,
      };
    }
    const duplicate = accepted.find((row) => (
      row.start_date === period.start_date && row.end_date === period.end_date
    ));
    if (duplicate) {
      return {
        complete: true, days: null, valid: false,
        message: lang === "de"
          ? `Dieser Zeitraum ist identisch mit ${periodLabel(duplicate, lang)}.`
          : `This period duplicates ${periodLabel(duplicate, lang)}.`,
      };
    }
    const draftConflict = accepted.find((row) => periodsOverlap(period, row));
    if (draftConflict) {
      return {
        complete: true, days: null, valid: false,
        message: lang === "de"
          ? `Dieser Zeitraum überschneidet sich mit ${periodLabel(draftConflict, lang)}.`
          : `This period overlaps ${periodLabel(draftConflict, lang)}.`,
      };
    }
    const existingConflict = existing.find((row) => periodsOverlap(period, row));
    if (existingConflict) {
      return {
        complete: true, days: null, valid: false,
        message: lang === "de"
          ? `Dieser Zeitraum überschneidet sich mit dem bestehenden Antrag ${periodLabel(existingConflict, lang)}.`
          : `This period overlaps the existing request ${periodLabel(existingConflict, lang)}.`,
      };
    }
    const days = daysInPeriod(period);
    accepted.push(period);
    return { complete: true, days, valid: true, message: "" };
  });
  return {
    periods: analyses,
    totalDays: analyses.reduce((sum, row) => sum + (row.valid ? row.days : 0), 0),
    valid: analyses.length > 0 && analyses.every((row) => row.valid),
  };
}

const COPY = {
  de: {
    title: "Jahresurlaubsplan", hint: "Mehrere Urlaubszeiträume gemeinsam planen und einzeln prüfen lassen.",
    year: "Jahr", from: "Von", to: "Bis", note: "Notiz zum Plan (optional)",
    add: "Zeitraum hinzufügen", send: "Jahresplan senden", days: "Kalendertage", total: "Gesamt",
    mine: "Meine Urlaubspläne und Anträge", empty: "Noch keine Urlaubsanträge.", plan: "Jahresplan",
    answer: "Antwort", answerSend: "Antwort senden", withdraw: "Zurückziehen",
    sent: "Jahresurlaubsplan wurde gesendet.", replied: "Antwort wurde gesendet.", withdrawn: "Antrag wurde zurückgezogen.",
    invalid: "Bitte gültige, nicht überlappende Zeiträume innerhalb des ausgewählten Jahres angeben.",
  },
  en: {
    title: "Annual leave plan", hint: "Plan several leave periods together and have each reviewed separately.",
    year: "Year", from: "From", to: "To", note: "Plan note (optional)",
    add: "Add period", send: "Send annual plan", days: "calendar days", total: "Total",
    mine: "My leave plans and requests", empty: "No leave requests yet.", plan: "Annual plan",
    answer: "Reply", answerSend: "Send reply", withdraw: "Withdraw",
    sent: "Annual leave plan sent.", replied: "Reply sent.", withdrawn: "Request withdrawn.",
    invalid: "Enter valid, non-overlapping periods within the selected year.",
  },
};

export default function LeaveSelfService() {
  const { lang } = useI18n();
  const copy = COPY[lang === "de" ? "de" : "en"];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ year: currentYear(), periods: [emptyPeriod()], note: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/me/leave-requests"); setRows(data.leave_requests || []); setError(""); }
    catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const periodAnalysis = useMemo(
    () => analyzeLeavePeriods(form.periods, form.year, rows, lang),
    [form.periods, form.year, lang, rows],
  );
  const totalDays = periodAnalysis.totalDays;
  const groups = useMemo(() => {
    const result = []; const byId = new Map();
    rows.forEach((row) => {
      const id = row.leave_plan_id || `single:${row.id}`;
      if (!byId.has(id)) { const group = { id, year: row.leave_plan_year, rows: [] }; byId.set(id, group); result.push(group); }
      byId.get(id).rows.push(row);
    });
    result.forEach((group) => group.rows.sort((a, b) => (a.period_index || 0) - (b.period_index || 0)));
    return result;
  }, [rows]);

  const updatePeriod = (index, key, value) => setForm((current) => ({
    ...current, periods: current.periods.map((period, position) => position === index ? { ...period, [key]: value } : period),
  }));
  const submit = async (event) => {
    event.preventDefault(); setNotice(""); setError("");
    if (!periodAnalysis.valid) { setError(copy.invalid); return; }
    setBusy(true);
    try {
      await api.post("/me/leave-plans", { year: Number(form.year), periods: form.periods, note: form.note.trim() || null });
      setForm({ year: form.year, periods: [emptyPeriod()], note: "" }); setNotice(copy.sent); await load();
    } catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reply = async (id) => {
    const message = (answers[id] || "").trim(); if (!message) return;
    setBusy(true); setError("");
    try { await api.post(`/me/leave-requests/${id}/reply`, { message }); setAnswers((current) => ({ ...current, [id]: "" })); setNotice(copy.replied); await load(); }
    catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    setBusy(true); setError("");
    try { await api.post(`/me/leave-requests/${id}/cancel`); setNotice(copy.withdrawn); await load(); }
    catch (requestError) { setError(apiError(requestError.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return <section className="pc-card p-5 mt-5 max-w-5xl" data-testid="leave-self-service">
    <header className="flex items-start gap-3"><span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ color: "var(--pc-cyan)", background: "var(--pc-cyan-dim)" }}><CalendarRange size={18} /></span><div><h2 className="font-display font-semibold">{copy.title}</h2><p className="text-xs mt-1" style={{ color: "var(--pc-muted)" }}>{copy.hint}</p></div></header>
    <form onSubmit={submit} className="mt-5" data-testid="leave-plan-form">
      <label className="text-xs block max-w-40" style={{ color: "var(--pc-muted)" }}>{copy.year}<select className="pc-input mt-1 w-full" value={form.year} onChange={(event) => setForm({ ...form, year: Number(event.target.value), periods: [emptyPeriod()] })}>{Array.from({ length: 5 }, (_, index) => currentYear() - 1 + index).map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
      <div className="grid gap-2 mt-3">{form.periods.map((period, index) => {
        const analysis = periodAnalysis.periods[index];
        return <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end rounded-xl p-3 border" style={{ background: "var(--pc-card-inset)", borderColor: analysis.message ? "var(--pc-score-poor)" : "transparent" }} data-testid={`leave-plan-period-${index}`} data-valid={analysis.valid ? "true" : "false"}><label className="text-xs" style={{ color: "var(--pc-muted)" }}>{copy.from}<input required aria-invalid={analysis.message ? "true" : undefined} type="date" min={`${form.year}-01-01`} max={`${form.year}-12-31`} className="pc-input mt-1 w-full" value={period.start_date} onChange={(event) => updatePeriod(index, "start_date", event.target.value)} /></label><label className="text-xs" style={{ color: "var(--pc-muted)" }}>{copy.to}<input required aria-invalid={analysis.message ? "true" : undefined} type="date" min={period.start_date || `${form.year}-01-01`} max={`${form.year}-12-31`} className="pc-input mt-1 w-full" value={period.end_date} onChange={(event) => updatePeriod(index, "end_date", event.target.value)} /></label><button type="button" aria-label={lang === "de" ? "Zeitraum entfernen" : "Remove period"} className="pc-btn pc-btn-ghost" disabled={form.periods.length === 1} onClick={() => setForm((current) => ({ ...current, periods: current.periods.filter((_, position) => position !== index) }))}><Trash2 size={14} /></button>{analysis.valid && <p className="col-span-3 text-[11px]" style={{ color: "var(--pc-muted)" }}>{analysis.days} {copy.days}</p>}{analysis.message && <p className="col-span-3 text-[11px]" role="alert" style={{ color: "var(--pc-score-poor)" }}>{analysis.message}</p>}</div>;
      })}</div>
      <div className="flex flex-wrap items-center gap-2 mt-3"><button type="button" className="pc-btn pc-btn-ghost flex items-center gap-1.5" onClick={() => setForm((current) => ({ ...current, periods: [...current.periods, emptyPeriod()] }))}><Plus size={14} />{copy.add}</button><span className="text-xs font-semibold ml-auto">{copy.total}: {totalDays} {copy.days}</span></div>
      <label className="text-xs block mt-3" style={{ color: "var(--pc-muted)" }}>{copy.note}<textarea className="pc-input mt-1 w-full" rows="2" maxLength="2000" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} /></label>
      <div className="flex justify-end mt-3"><button disabled={busy || !periodAnalysis.valid} className="pc-btn pc-btn-primary flex items-center gap-2" type="submit"><Send size={14} />{copy.send}</button></div>
    </form>
    {(notice || error) && <p className="text-xs mt-3" role="status" style={{ color: error ? "var(--pc-score-poor)" : "var(--pc-score-good)" }}>{error || notice}</p>}
    <h3 className="text-sm font-semibold mt-6 mb-2">{copy.mine}</h3>
    <div className="grid gap-3" data-testid="my-leave-requests">{groups.length === 0 && <p className="text-xs py-3" style={{ color: "var(--pc-muted)" }}>{copy.empty}</p>}{groups.map((group) => <section key={group.id} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--pc-border)" }}><header className="px-3 py-2 text-xs font-semibold" style={{ background: "var(--pc-card-inset)" }}>{group.year ? `${copy.plan} ${group.year} · ${group.rows.length} ${lang === "de" ? "Zeiträume" : "periods"}` : copy.mine}</header>{group.rows.map((row) => <article key={row.id} className="p-3 border-t" style={{ borderColor: "var(--pc-border)" }} data-testid={`leave-request-${row.id}`}><div className="flex flex-wrap items-center gap-2"><strong className="text-sm">{row.start_date} – {row.end_date}</strong><span className="text-[11px]" style={{ color: "var(--pc-muted)" }}>{row.calendar_days} {copy.days}</span><span className="pc-chip text-[10px]" style={{ color: STATUS_TONE[row.status] || "var(--pc-muted)", background: "var(--pc-card-inset)" }}>{lang === "de" ? row.status_label : row.status_label_en}</span>{["pending", "needs_info"].includes(row.status) && <button type="button" disabled={busy} onClick={() => cancel(row.id)} className="pc-btn pc-btn-ghost ml-auto flex items-center gap-1.5" style={{ padding: "6px 9px", fontSize: 11 }}><X size={12} />{copy.withdraw}</button>}</div>{row.employee_note && <p className="text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{row.employee_note}</p>}{row.admin_response && <p className="text-xs mt-2 px-3 py-2 rounded-lg" style={{ background: "var(--pc-card-inset)", color: "var(--pc-text)" }}><MessageSquare size={12} className="inline mr-1.5" />{row.admin_response}</p>}{row.status === "needs_info" && <div className="flex gap-2 mt-2"><input aria-label={copy.answer} className="pc-input flex-1" value={answers[row.id] || ""} onChange={(event) => setAnswers((current) => ({ ...current, [row.id]: event.target.value }))} /><button type="button" disabled={busy || !(answers[row.id] || "").trim()} onClick={() => reply(row.id)} className="pc-btn pc-btn-primary">{copy.answerSend}</button></div>}</article>)}</section>)}</div>
  </section>;
}
